import type { OptimizationPlanPublication } from '../../types/optimizationOutput'

type StepState = 'done' | 'active' | 'pending'

interface Props {
  publication: OptimizationPlanPublication | null | undefined
  activeStep: number
  onStepChange: (step: number) => void
}

export default function PublicationWorkflow({ publication, activeStep, onStepChange }: Props) {
  const status = publication?.status
  const states: StepState[] = !publication
    ? ['active', 'pending', 'pending', 'pending']
    : status === 'review_requested'
      ? ['done', 'active', 'pending', 'pending']
      : status === 'approved'
        ? ['done', 'done', 'active', 'pending']
        : status === 'published'
          ? ['done', 'done', 'done', 'active']
          : ['done', 'pending', 'pending', 'pending']
  const steps = [
    ['Revue', 'Préparer la version à contrôler'],
    ['Approbation', 'Valider les contrôles du plan'],
    ['Publication', 'Figer la version diffusée'],
    ['Partage', 'Créer un lien pour le destinataire'],
  ]

  return <section className="mt-5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/70 p-4" aria-label="Cycle de diffusion du plan">
    <div className="mb-3 flex items-baseline justify-between gap-3">
      <h3 className="text-sm font-bold text-slate-900 dark:text-white">Cycle de diffusion</h3>
    </div>
    <ol className="flex items-start overflow-x-auto pb-1">
      {steps.map(([label, description], index) => {
        const state = states[index]
        const stateLabel = state === 'done' ? 'Terminé' : state === 'active' ? 'À faire' : 'À venir'
        const accessible = state !== 'pending'
        return <li key={label} className="flex min-w-[132px] flex-1 items-start last:flex-none">
          <button type="button" disabled={!accessible} onClick={() => onStepChange(index)} className={`group flex min-w-0 flex-col items-center text-center disabled:cursor-not-allowed ${activeStep === index ? 'text-slate-900 dark:text-white' : state === 'pending' ? 'text-slate-600 dark:text-slate-500' : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white'}`} aria-current={activeStep === index ? 'step' : undefined}>
            <span className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${state === 'done' ? 'bg-emerald-500 text-white' : state === 'active' ? 'bg-blue-500 text-white ring-4 ring-blue-500/20' : 'bg-slate-700 text-slate-500 dark:text-slate-400'}`}>{state === 'done' ? '✓' : index + 1}</span>
            <span className="mt-2 text-xs font-semibold">{label}</span>
            <span className={state === 'done' ? 'mt-0.5 text-[10px] text-emerald-700 dark:text-emerald-300' : state === 'active' ? 'mt-0.5 text-[10px] text-blue-700 dark:text-blue-300' : 'mt-0.5 text-[10px] text-slate-600 dark:text-slate-500'}>{stateLabel}</span>
            <span className="mt-1 hidden text-[10px] text-slate-500 dark:text-slate-400 xl:block">{description}</span>
          </button>
          {index < steps.length - 1 && <span aria-hidden="true" className={`mt-3 h-px min-w-5 flex-1 border-t border-dashed ${state === 'done' ? 'border-emerald-500' : 'border-slate-200 dark:border-slate-700'}`} />}
        </li>
      })}
    </ol>
  </section>
}
